import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { TabBar, TabPanel } from '../patterns';
import { SprayMachine } from '../../services/sprayService';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import SprayMachineList from './SprayMachineList';
import SprayMachineForm from './SprayMachineForm';
import MachineCalibrationsTab from './MachineCalibrationsTab';

const SprayMachinesPage: React.FC = () => {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [editMachine, setEditMachine] = useState<SprayMachine | undefined>();
  const [refreshKey, setRefreshKey] = useState(0);
  // Controlled Tabs activeKey so the page wrapper's breadcrumb effect
  // re-fires on tab switches. With uncontrolled Tabs (defaultActiveKey),
  // switching tabs would unmount the active panel — and the children's
  // useBreadcrumb cleanup would clear the crumbs while the wrapper's
  // effect, with stable deps, never re-fired to repopulate them.
  const [activeTab, setActiveTab] = useState<'profile' | 'calibrations'>('profile');
  const handleTabSelect = useCallback((k: string) => {
    if (k === 'profile' || k === 'calibrations') setActiveTab(k);
  }, []);

  const handleAdd = useCallback(() => { setEditMachine(undefined); setShowForm(true); setActiveTab('profile'); }, []);
  const handleEdit = useCallback((machine: SprayMachine) => { setEditMachine(machine); setShowForm(true); setActiveTab('profile'); }, []);
  const handleCancel = useCallback(() => { setShowForm(false); setEditMachine(undefined); }, []);
  const handleSave = useCallback(() => {
    setShowForm(false);
    setEditMachine(undefined);
    setRefreshKey(k => k + 1);
  }, []);

  // Breadcrumb push lives on the page wrapper so it survives the Tabs
  // unmount-on-exit between Profile and Calibrations. The activeTab
  // dependency below is what keeps the wrapper's effect re-firing on
  // tab switches — without it the children's cleanup would leave the
  // breadcrumbs empty when they unmount.
  const breadcrumbLeaf = !showForm
    ? t('spray.machines.title')
    : !editMachine
    ? t('spray.pageHeader.actions.newMachine')
    : activeTab === 'calibrations'
    ? t('spray.machines.calibrationsTitle', { name: editMachine.name, defaultValue: '{{name}} — Calibrations' })
    : t('spray.machines.editTitle', { name: editMachine.name, defaultValue: 'Edit {{name}}' });
  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('spray.title'), to: '/protection' },
    { label: t('spray.machines.title'), to: '/protection/machines' },
    { label: breadcrumbLeaf },
  ]);

  if (!showForm) {
    return <SprayMachineList key={refreshKey} onAdd={handleAdd} onEdit={handleEdit} />;
  }

  // Creating a new machine — no calibrations tab yet (no machine id).
  if (!editMachine) {
    return <SprayMachineForm onSave={handleSave} onCancel={handleCancel} />;
  }

  // Editing an existing machine — show Profile + Calibrations tabs.
  // The pat-* TabBar mounts/unmounts panels synchronously on activeKey
  // change, so the children's useBreadcrumb cleanup runs in the same
  // render cycle as the wrapper's effect re-fire. No transition race —
  // no need for the `transition={false}` workaround the react-bootstrap
  // Tabs implementation required.
  return (
    <TabBar activeKey={activeTab} onSelect={handleTabSelect}>
      <TabPanel id="profile" title={t('spray.machines.tabs.profile')}>
        <SprayMachineForm machine={editMachine} onSave={handleSave} onCancel={handleCancel} />
      </TabPanel>
      <TabPanel id="calibrations" title={t('spray.machines.tabs.calibrations')}>
        <MachineCalibrationsTab machineId={editMachine._id} machineName={editMachine.name} />
      </TabPanel>
    </TabBar>
  );
};

export default SprayMachinesPage;
